import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, ClipboardCheck, PenTool } from "lucide-react";
import ProgressRing from "@/components/ProgressRing";
import BucketWearMap from "@/components/BucketWearMap";
import AnimatedCounter from "@/components/AnimatedCounter";

/**
 * SignoffCommandCentre — a cinematic, mission-control replacement for the
 * flat ReviewSummary. Shown at the top of an inspection drawer whenever the
 * inspection has loaded.
 *
 * Visual anatomy:
 *   1. Header — big radial "answered vs total" ring + colour-tinted counts
 *   2. Bucket wear map (SVG) — each tooth / lip / wing lit with its wear tier
 *   3. Issues rail — pulsing red cards for anything flagged as ISSUE / PPF
 *   4. Per-section rings — each section shows a mini progress ring + expand
 */
export default function SignoffCommandCentre({ insp, allItems, itemsState }) {
  const [openSecs, setOpenSecs] = useState(() => new Set((insp?.sections || []).map((s) => s.id)));

  const totals = useMemo(() => summarize(allItems, itemsState), [allItems, itemsState]);
  const answered = allItems.length - totals.open;
  const answeredPct = allItems.length > 0 ? (answered / allItems.length) * 100 : 0;

  const wearItems = useMemo(
    () => allItems.filter((it) => it.kind === "wear_pct").map((it) => ({ ...it, status: itemsState[it.id]?.status })),
    [allItems, itemsState],
  );
  const issueItems = useMemo(
    () => allItems.filter((it) => String(itemsState[it.id]?.status ?? "").toLowerCase() === "issue"),
    [allItems, itemsState],
  );

  const toggleSec = (id) => setOpenSecs((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div
      className="relative rounded-2xl overflow-hidden card-spotlight"
      style={{
        background: "linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(14,14,14,0.9) 100%)",
        border: "1px solid rgba(245,130,32,0.35)",
        boxShadow: "0 0 0 1px rgba(245,130,32,0.12), 0 24px 60px -20px rgba(245,130,32,0.35)",
      }}
      data-testid="review-summary"
    >
      {/* Aurora strip along the top edge of the panel */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, var(--primary) 30%, #ffd280 50%, var(--primary) 70%, transparent)",
          filter: "drop-shadow(0 0 12px rgba(245,130,32,0.55))",
        }}
      />

      {/* HEADER — mission-control style */}
      <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-5 md:gap-8 items-center">
        <ProgressRing
          size={140}
          stroke={10}
          value={answeredPct}
          glow={answeredPct < 100}
          color={answeredPct === 100 ? "#4ade80" : "var(--primary)"}
        >
          <div className="text-[9px] uppercase tracking-widest text-muted font-black leading-none mb-1">
            Answered
          </div>
          <div className="font-display font-black tabular-nums leading-none" style={{ fontSize: 34, color: answeredPct === 100 ? "#4ade80" : "var(--primary)" }}>
            <AnimatedCounter value={answered} />
            <span className="text-[0.55em] text-muted">/{allItems.length}</span>
          </div>
          <div className="text-[9px] uppercase tracking-widest text-muted font-black mt-1">
            <AnimatedCounter value={answeredPct} format={(n) => `${n}%`} />
          </div>
        </ProgressRing>

        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.4em] font-black" style={{ color: "var(--primary)" }}>
            <ClipboardCheck className="w-3 h-3 inline -mt-0.5 mr-1.5" />
            Sign-off command centre
          </div>
          <div className="font-display font-black text-2xl md:text-[28px] leading-tight mt-1 tracking-tight">
            EVERY ITEM YOUR CREW COMPLETED
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
            <KpiTile label="OK" value={totals.ok} color="#4ade80" />
            <KpiTile label="Issue" value={totals.issue} color="#ff9b9b" glow={totals.issue > 0} />
            <KpiTile label="N/A" value={totals.na} color="#a1a1aa" />
            <KpiTile label="Planning Requests" value={totals.ppf} color="#ffb347" />
            <KpiTile label="Open" value={totals.open} color="#f58220" glow={totals.open > 0} />
          </div>
          {wearItems.length > 0 && (
            <div className="mt-2 grid grid-cols-4 md:grid-cols-5 gap-2">
              <KpiTile label="NEW" value={totals.wearNew} color="#4ade80" small />
              <KpiTile label="25%" value={totals.w25} color="#4ade80" small />
              <KpiTile label="50%" value={totals.w50} color="#facc15" small />
              <KpiTile label="75%" value={totals.w75} color="#ffb347" small />
              <KpiTile label="C/O" value={totals.w100} color="#ff9b9b" small />
            </div>
          )}
        </div>
      </div>

      {/* BUCKET WEAR MAP — only for diggers */}
      {wearItems.length > 0 && (
        <div className="px-5 md:px-6 pb-5">
          <BucketWearMap items={wearItems} />
        </div>
      )}

      {/* ISSUES RAIL — pulsing red cards */}
      {issueItems.length > 0 && (
        <div className="px-5 md:px-6 pb-5" data-testid="cc-issues-rail">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black mb-2" style={{ color: "#ff9b9b" }}>
            <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />
            Items flagged as issue · {issueItems.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {issueItems.slice(0, 6).map((it) => {
              const st = itemsState[it.id] || {};
              return (
                <div
                  key={it.id}
                  className="issue-pulse rounded-lg p-3"
                  style={{
                    background: "rgba(255,155,155,0.08)",
                    border: "1px solid rgba(255,155,155,0.3)",
                  }}
                  data-testid={`review-issue-${it.id}`}
                >
                  <div className="text-xs font-black leading-tight">{it.label}</div>
                  {(st.action_priority || st.action_required) && (
                    <div className="text-[10px] text-muted mt-1">
                      {st.action_priority && <span className="uppercase tracking-widest mr-1">P{st.action_priority}</span>}
                      {st.action_required}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PER-SECTION RINGS */}
      <div className="px-5 md:px-6 pb-5 md:pb-6 space-y-2" data-testid="cc-sections">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-muted">Sections</div>
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpenSecs(new Set((insp?.sections || []).map((s) => s.id)))}
              className="text-[10px] uppercase tracking-widest font-black text-muted hover:text-white transition"
              data-testid="review-expand-all"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={() => setOpenSecs(new Set())}
              className="text-[10px] uppercase tracking-widest font-black text-muted hover:text-white transition"
              data-testid="review-collapse-all"
            >
              Collapse all
            </button>
          </div>
        </div>
        {(insp?.sections || []).map((sec) => {
          const secItems = sec.items || [];
          const sc = summarize(secItems, itemsState);
          const secAnswered = secItems.length - sc.open;
          const secPct = secItems.length > 0 ? (secAnswered / secItems.length) * 100 : 0;
          const isOpen = openSecs.has(sec.id);
          const ringColor = sc.issue > 0 ? "#ff9b9b" : secPct === 100 ? "#4ade80" : "var(--primary)";
          return (
            <div
              key={sec.id}
              className="rounded-xl overflow-hidden"
              style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid={`review-section-${sec.id}`}
            >
              <button
                type="button"
                onClick={() => toggleSec(sec.id)}
                className="w-full flex items-center gap-4 p-3 md:p-4 text-left hover:bg-white/[0.03] transition"
                data-testid={`review-section-toggle-${sec.id}`}
              >
                <ProgressRing size={44} stroke={4} value={secPct} color={ringColor}>
                  <div className="text-[9px] tabular-nums font-black" style={{ color: ringColor }}>
                    {Math.round(secPct)}%
                  </div>
                </ProgressRing>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-black text-muted">{sec.name}</div>
                  <div className="text-sm mt-0.5 tabular-nums">
                    <span className="font-black" style={{ color: ringColor }}>
                      {secAnswered}/{secItems.length}
                    </span>
                    <span className="text-muted"> answered</span>
                    {sc.issue > 0 && <span className="ml-2 text-[10px] uppercase tracking-widest" style={{ color: "#ff9b9b" }}>· {sc.issue} issue</span>}
                  </div>
                </div>
                <div className="hidden md:flex flex-wrap gap-1 justify-end">
                  <MiniChip label="OK" n={sc.ok} c="#4ade80" />
                  <MiniChip label="ISSUE" n={sc.issue} c="#ff9b9b" />
                  <MiniChip label="N/A" n={sc.na} c="#a1a1aa" />
                  <MiniChip label="Plan Req" n={sc.ppf} c="#ffb347" />
                  <MiniChip label="NEW" n={sc.wearNew} c="#4ade80" />
                  <MiniChip label="25%" n={sc.w25} c="#4ade80" />
                  <MiniChip label="50%" n={sc.w50} c="#facc15" />
                  <MiniChip label="75%" n={sc.w75} c="#ffb347" />
                  <MiniChip label="C/O" n={sc.w100} c="#ff9b9b" />
                  <MiniChip label="OPEN" n={sc.open} c="#f58220" />
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 shrink-0 text-muted" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted" />}
              </button>
              {isOpen && (
                <div className="border-t border-white/5 divide-y divide-white/[0.04]">
                  {secItems.map((it) => {
                    const pill = itemPill(it, itemsState);
                    const st = itemsState[it.id] || {};
                    return (
                      <div
                        key={it.id}
                        className="flex items-start gap-3 px-4 py-2 hover:bg-white/[0.03] transition"
                        data-testid={`review-item-${it.id}`}
                      >
                        <span
                          className="pill wear-fill !text-[9px] !py-0.5 !px-1.5 shrink-0 mt-0.5 tabular-nums"
                          style={{
                            background: `${pill.color}22`,
                            border: `1px solid ${pill.color}55`,
                            color: pill.color,
                            minWidth: 48,
                            justifyContent: "center",
                            ["--fill"]: `${pill.fillPct}%`,
                          }}
                        >
                          {pill.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-black leading-tight">{it.label}</div>
                          {st.action_required && (
                            <div className="text-[10px] text-muted mt-0.5 line-clamp-2">
                              {st.action_priority && <span className="uppercase tracking-widest mr-1">P{st.action_priority} ·</span>}
                              {st.action_required}
                            </div>
                          )}
                          {st.clicked_by_name && (
                            <div className="text-[10px] text-muted mt-0.5">
                              {st.clicked_by_name}
                              {st.clicked_at && ` · ${fmtWhen(st.clicked_at)}`}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="px-5 md:px-6 pb-4 text-[10px] uppercase tracking-widest text-muted flex items-center gap-2">
        <PenTool className="w-3 h-3" />
        Scroll down to complete SMU · sign · approve
      </div>
    </div>
  );
}

/* ---------------- Helpers ---------------- */
function summarize(items, itemsState) {
  const c = { ok:0, issue:0, na:0, ppf:0, open:0, wearNew:0, w25:0, w50:0, w75:0, w100:0 };
  (items || []).forEach((it) => {
    const st = itemsState?.[it.id]?.status;
    const s = String(st ?? "").toLowerCase();
    if (it.kind === "wear_pct") {
      if (s === "new" || s === "0") c.wearNew++;
      else if (s === "25") c.w25++;
      else if (s === "50") c.w50++;
      else if (s === "75") c.w75++;
      else if (s === "100") c.w100++;
      else c.open++;
      return;
    }
    if (s === "ok") c.ok++;
    else if (s === "issue") { c.issue++; if (itemsState?.[it.id]?.action_priority) c.ppf++; }
    else if (s === "na") c.na++;
    else c.open++;
  });
  return c;
}

function itemPill(it, itemsState) {
  const st = itemsState?.[it.id] || {};
  const s = String(st.status ?? "").toLowerCase();
  if (it.kind === "wear_pct") {
    if (s === "new" || s === "0") return { label: "NEW", color: "#4ade80", fillPct: 5 };
    if (s === "25") return { label: "25%", color: "#4ade80", fillPct: 25 };
    if (s === "50") return { label: "50%", color: "#facc15", fillPct: 50 };
    if (s === "75") return { label: "75%", color: "#ffb347", fillPct: 75 };
    if (s === "100") return { label: "C/O", color: "#ff9b9b", fillPct: 100 };
    return { label: "OPEN", color: "#f58220", fillPct: 0 };
  }
  if (s === "ok") return { label: "OK", color: "#4ade80", fillPct: 100 };
  if (s === "issue") return { label: "ISSUE", color: "#ff9b9b", fillPct: 100 };
  if (s === "na") return { label: "N/A", color: "#a1a1aa", fillPct: 100 };
  return { label: "OPEN", color: "#f58220", fillPct: 0 };
}

function fmtWhen(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
}

function KpiTile({ label, value, color, glow = false, small = false }) {
  return (
    <div
      className={`relative rounded-lg px-3 py-2 ${glow ? "hud-pulse" : ""}`}
      style={{
        background: `${color}12`,
        border: `1px solid ${color}44`,
        color,
      }}
    >
      <div className={`${small ? "text-[8px]" : "text-[9px]"} uppercase tracking-widest font-black opacity-80`}>
        {label}
      </div>
      <div className={`font-display font-black tabular-nums leading-none mt-1 ${small ? "text-lg" : "text-2xl"}`}>
        <AnimatedCounter value={value} />
      </div>
    </div>
  );
}

function MiniChip({ label, n, c }) {
  if (n === 0) return null;
  return (
    <span
      className="pill !text-[9px] !py-0.5 !px-1.5 tabular-nums"
      style={{ background: `${c}22`, border: `1px solid ${c}55`, color: c }}
    >
      {label} · {n}
    </span>
  );
}
